import React, { useEffect, useRef, useState } from 'react';

import { IconSpinner, LayoutComposer } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import { validProps } from './Field';

import SectionBuilder from './SectionBuilder/SectionBuilder.js';
import StaticPage from './StaticPage.js';

import css from './PageBuilder.module.css';
import RenderFeaturedLocations from './SectionBuilder/RenderFeaturedLocations.js';
import SectionBlogs from './SectionBuilder/SectionBlogs.js';
import { filterOutBlogSection } from '../../util/genericHelpers.js';
import SectionHost from './SectionBuilder/SectionHost.js';
import SectionFAQ from './SectionBuilder/SectionFAQ.js';
import RenderFeaturedCategories from './SectionBuilder/RenderFeaturedCategories.js';
import SectionReviews from './SectionBuilder/SectionReviews.js';

const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  // pageTitle is used for <title> tag in addition to page schema for SEO
  const title = validProps(pageTitle, fieldOptions)?.content;
  // pageDescription is used for different <meta> tags in addition to page schema for SEO
  const description = validProps(pageDescription, fieldOptions)?.content;
  // Data used when the page is shared in social media services
  const openGraph = validProps(socialSharing, fieldOptions);
  // We add OpenGraph image as schema image if it exists.
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org (This template uses JSON-LD format)
  //
  // In addition to this schema data for search engines, src/components/Page/Page.js adds some extra schemas
  // Read more about schema:
  // - https://schema.org/
  // - https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
  const pageSchemaForSEO = {
    '@context': 'http://schema.org',
    '@type': schemaType || 'WebPage',
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

//////////////////
// Page Builder //
//////////////////

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * PageBuilder can be used to build content pages using page-asset.json.
 *
 * Note: props can include a lot of things that depend on
 * - pageAssetsData: json asset that contains instructions how to build the page content
 *   - asset should contain an array of _sections_, which might contain _fields_ and an array of _blocks_
 *     - _blocks_ can also contain _fields_
 * - fallbackPage: component. If asset loading fails, this is used instead.
 * - options: extra mapping of 3 level of sub components
 *   - sectionComponents: { ['my-section-type']: { component: MySection } }
 *   - blockComponents: { ['my-component-type']: { component: MyBlock } }
 *   - fieldComponents: { ['my-field-type']: { component: MyField, pickValidProps: data => Number.isInteger(data.content) ? { content: data.content } : {} }
 *     - fields have this pickValidProps as an extra requirement for data validation.
 * - pageProps: props that are passed to src/components/Page/Page.js component
 *
 * @param {Object} props
 * @param {Object} props.pageAssetsData
 * @param {Array<Object>} props.pageAssetsData.sections
 * @param {Object} props.pageAssetsData.meta
 * @param {Object} props.pageAssetsData.meta.pageTitle
 * @param {Object} props.pageAssetsData.meta.pageDescription
 * @param {Object} props.pageAssetsData.meta.socialSharing
 * @param {boolean?} props.inProgress
 * @param {Object?} props.error
 * @param {ReactNode?} props.fallbackPage
 * @param {string} props.schemaType type from schema.org (e.g. 'Article', 'Website')
 * @param {string?} props.currentPage name of the current page based on route configuration
 * @param {Object} props.options
 * @param {Object<string,FieldComponentConfig>} props.options.fieldComponents custom field components
 * @returns {JSX.Element} page component
 */
const PageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    isLandingPage,
    ...pageProps
  } = props;

  const [showTopbarSearch, setShowTopbarSearch] = useState(false);
  const heroSearchRef = useRef(null);

  useEffect(() => {
    if (!isLandingPage) {
      setShowTopbarSearch(false);
      return undefined;
    }

    const getTopbarHeight = () => {
      const topbarHeight = getComputedStyle(document.documentElement).getPropertyValue(
        '--topbarHeightDesktop'
      );
      const parsedHeight = Number.parseFloat(topbarHeight);
      return Number.isFinite(parsedHeight) ? parsedHeight : 0;
    };

    const handleScroll = () => {
      if (heroSearchRef.current) {
        const { bottom } = heroSearchRef.current.getBoundingClientRect();
        setShowTopbarSearch(bottom <= getTopbarHeight());
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isLandingPage]);

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  // Page asset contains UI info and metadata related to it.
  // - "sections" (data that goes inside <body>)
  // - "meta" (which is data that goes inside <head>)
  const { sections = [], meta = {} } = pageAssetsData || {};
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents);

  const layoutAreas = `
    topbar
    main
    footer
  `;

  const filteredSections = filterOutBlogSection(sections);

  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage={currentPage} showTopbarSearch={showTopbarSearch} />
              </Topbar>
              <Main as="main" className={css.main}>
                {filteredSections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    {filteredSections.map((section, index) => {
                      const heroSearchRefMaybe =
                        index === 0 && section.sectionType === 'hero' ? heroSearchRef : null;

                      return (
                        <React.Fragment key={index}>
                          <SectionBuilder
                            sections={[section]}
                            options={options}
                            isLandingPage={isLandingPage}
                            heroSearchRef={heroSearchRefMaybe}
                          />

                          {index === 2 && <RenderFeaturedCategories />}
                          {index === 2 && <RenderFeaturedLocations />}
                          {index === 2 && <SectionBlogs />}
                          {index === 2 && <SectionReviews />}
                          {index === 2 && <SectionFAQ />}
                          {index === 2 && <SectionHost />}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default PageBuilder;
